import { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';

/**
 * Fullscreen QR-code scanner.
 *
 * Uses the browser's native `BarcodeDetector` API (Chrome / Android,
 * Edge, Samsung Internet) to read QR codes off the rear camera in real
 * time. iOS Safari doesn't expose BarcodeDetector — we surface a clean
 * "not supported on this device" state with guidance to use the photo
 * path instead. (Adding `jsQR` as a fallback is a 5-min follow-up if
 * iOS support becomes a real need.)
 *
 * Usage:
 *
 *   {scannerOpen && (
 *     <QrScanner
 *       onScan={(rawValue) => { ... apply pre-fill ...; setScannerOpen(false); }}
 *       onClose={() => setScannerOpen(false)}
 *     />
 *   )}
 *
 * Lifecycle:
 *   - Mounts → requests camera (getUserMedia, environment-facing)
 *   - Polls BarcodeDetector on a hidden canvas every 250ms
 *   - First successful detection fires onScan(rawValue) once and stops
 *   - Always cleans up the camera stream on unmount, even if user
 *     swipes away mid-scan
 */
export default function QrScanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const intervalRef = useRef(null);
  // Once we've fired onScan we don't want subsequent detection cycles
  // to fire it again before the parent unmounts us — debounce via ref.
  const scannedRef = useRef(false);

  const [error, setError] = useState(null);
  const [unsupported, setUnsupported] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      // Feature-check first — saves the camera-permission prompt on
      // platforms that can't do anything useful with it.
      if (typeof window === 'undefined' || !('BarcodeDetector' in window)) {
        setUnsupported(true);
        return;
      }
      try {
        // eslint-disable-next-line no-undef
        detectorRef.current = new BarcodeDetector({ formats: ['qr_code'] });
      } catch (err) {
        // Some Android browsers expose the symbol but throw on
        // construction (no QR-code support compiled in).
        setUnsupported(true);
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' }, // back camera preferred
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        // playsInline + muted are essential on iOS Safari to avoid the
        // OS hijacking the video into fullscreen mode.
        video.muted = true;
        video.setAttribute('playsinline', 'true');
        await video.play();
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.name === 'NotAllowedError'
            ? 'Camera access was denied. Allow it in your browser settings and try again.'
            : err?.message || 'Couldn’t start the camera.',
        );
        return;
      }

      // Poll the detector. 250ms feels responsive without burning
      // battery; QR detection is fast enough that one frame in four is
      // plenty.
      intervalRef.current = setInterval(async () => {
        if (cancelled || scannedRef.current) return;
        const video = videoRef.current;
        const detector = detectorRef.current;
        if (!video || !detector || video.readyState < 2) return;
        try {
          const codes = await detector.detect(video);
          if (codes && codes.length > 0 && !scannedRef.current) {
            scannedRef.current = true;
            onScan(codes[0].rawValue);
          }
        } catch {
          // Detection errors per-frame are noisy and often transient
          // (frame still loading, camera autofocusing). Ignore and try
          // again next tick.
        }
      }, 250);
    }

    start();

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
    // onScan is intentionally not in deps — we only want to set up the
    // scanner once. The ref-debounced scannedRef ensures we don't
    // double-fire on stale closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Top bar — close + "scanning" hint */}
      <header className="bg-amber-500 pt-safe-top px-4 pb-3 flex items-center min-h-touch">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close scanner"
          className="-ml-2 min-h-touch min-w-touch flex items-center justify-center text-white/90 active:text-white"
        >
          <X className="h-6 w-6" />
        </button>
        <p className="flex-1 text-center text-body font-semibold text-white">
          Scan ZIMRA QR
        </p>
        <span className="w-touch shrink-0" aria-hidden="true" />
      </header>

      <div className="relative flex-1 overflow-hidden">
        {!unsupported && !error && (
          <>
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              playsInline
              muted
            />
            {/* Hidden canvas — kept for any future fallback path that
                needs to draw the frame to extract pixel data (e.g. jsQR).
                Currently unused since BarcodeDetector reads the video
                element directly. */}
            <canvas ref={canvasRef} className="hidden" />

            {/* Viewfinder overlay — a square frame in the centre of the
                screen, semi-transparent backdrop around it, animated
                scan-line for visual feedback that the camera is live.
                pointer-events-none so taps go through to the close
                button if the user wants to bail mid-scan. */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="relative w-[70%] aspect-square max-w-sm">
                <div className="absolute inset-0 border-2 border-amber-400 rounded-card" />
                {/* Corner brackets — make the viewfinder feel scanner-y */}
                <span className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-amber-400 rounded-tl-card" />
                <span className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-amber-400 rounded-tr-card" />
                <span className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-amber-400 rounded-bl-card" />
                <span className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-amber-400 rounded-br-card" />
              </div>
            </div>

            <p className="absolute bottom-safe-bottom left-0 right-0 px-6 pb-6 text-center text-small text-white/85">
              Centre the QR code in the frame
            </p>
          </>
        )}

        {unsupported && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center text-white">
            <div className="w-14 h-14 rounded-pill bg-white/10 flex items-center justify-center mb-3">
              <X className="h-6 w-6 text-white/80" />
            </div>
            <p className="text-body font-semibold mb-1">QR scanning isn’t supported on this device</p>
            <p className="text-small text-white/70">
              iOS Safari doesn’t expose the QR scanner API. Tap “Take photo of receipt” instead — the photo gets attached and you can enter the details manually.
            </p>
          </div>
        )}

        {error && !unsupported && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center text-white">
            <Loader2 className="h-6 w-6 mb-3 text-white/80 animate-spin" />
            <p className="text-small text-white/85">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
